import { useCallback, useEffect, useRef } from 'react';
import { OutcomeView, useRoute, type ModuleScreenContext } from '@huaca/ui';
import { PostingBody, usePosting } from './PostingDetail';
import { DetailSkeleton } from './Skeleton';

/**
 * A posting, over the search rather than instead of it.
 *
 * Opening a posting used to be a navigation: the search screen unmounted, and coming back meant
 * refetching the list, the facets and the reader's place in it. With two independently scrolling
 * panes that is worse than it sounds — the pane scroll goes too, so returning from result 34 puts
 * you back at result 1 of a list you have already read.
 *
 * THE URL DOES NOT CHANGE SHAPE. `/postings/:slug/:siteId` is still the address, still shareable,
 * still what a bookmark holds. The search simply renders behind it, so closing the panel always
 * lands the reader in a working search — including on a link somebody else sent them.
 *
 * ACCESSIBILITY IS THE POINT OF THIS FILE, not the animation. A dialog that traps focus badly is
 * worse than no dialog: a keyboard reader tabs into a list they cannot see and cannot get out of.
 */

/** Everything focusable, in DOM order. Queried per keystroke — the panel's content changes as it loads. */
const FOCUSABLE =
  'a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])';

export interface PostingPanelProps {
  slug: string;
  siteId: string;
  /** The search that led here — the panel closes back onto exactly this query string. */
  search: string;
  ctx: ModuleScreenContext;
}

export function PostingPanel({ slug, siteId, search, ctx }: PostingPanelProps) {
  // The router's own `navigate`, not `ctx.navigate` — only this one takes `{ replace }`, and
  // replacing rather than pushing is what stops Back reopening the posting just dismissed.
  const { navigate } = useRoute();
  const { posting, outcome, reload } = usePosting(slug, siteId);
  const panel = useRef<HTMLDivElement>(null);
  const opener = useRef<Element | null>(null);

  /**
   * Close by REPLACING the history entry, not by pushing another one.
   *
   * Opening pushed an entry, so replacing it on close leaves the reader's Back button pointing at
   * whatever came before they opened the posting — rather than at the posting they just dismissed,
   * which would reopen it. `history.back()` would do the same thing only when there IS something to
   * go back to; on a shared link it would leave the site entirely.
   */
  const close = useCallback(() => {
    navigate(`/${search}`, { replace: true });
  }, [navigate, search]);

  // Remember what had focus BEFORE the panel painted, so it can be handed back on close. This is
  // the row the reader clicked; without it focus falls to the top of the document and a keyboard
  // reader loses their place entirely.
  useEffect(() => {
    opener.current = document.activeElement;
    const restore = opener.current;
    return () => {
      if (restore instanceof HTMLElement && document.contains(restore)) restore.focus();
    };
  }, []);

  // Focus moves INTO the panel on open. Without this the panel is announced and then the reader's
  // next Tab lands back in the search behind it.
  useEffect(() => {
    panel.current?.focus();
  }, []);

  /**
   * Escape closes; Tab cycles inside.
   *
   * The trap is a wrap rather than a block: Tab past the last control returns to the first, and
   * Shift+Tab before the first goes to the last. Blocking instead would strand focus on whichever
   * end the reader reached.
   */
  useEffect(() => {
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        event.preventDefault();
        close();
        return;
      }
      if (event.key !== 'Tab' || !panel.current) return;

      const focusable = [...panel.current.querySelectorAll<HTMLElement>(FOCUSABLE)];
      if (focusable.length === 0) {
        // Still loading, so there is nothing to cycle through — keep focus on the panel itself
        // rather than letting Tab escape into the search behind the scrim.
        event.preventDefault();
        panel.current.focus();
        return;
      }

      const first = focusable[0]!;
      const last = focusable[focusable.length - 1]!;
      const active = document.activeElement;

      if (event.shiftKey && (active === first || active === panel.current)) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && active === last) {
        event.preventDefault();
        first.focus();
      }
    };

    document.addEventListener('keydown', onKeyDown);
    return () => document.removeEventListener('keydown', onKeyDown);
  }, [close]);

  /**
   * Freeze what is behind the scrim.
   *
   * `inert` would be the right tool and is unavailable at the Safari 14 floor, so this is a class on
   * the root that CSS uses to stop the two result panes scrolling. Without it a wheel over the
   * scrim scrolls the list underneath — the panel looks modal and does not behave modally.
   */
  useEffect(() => {
    document.documentElement.classList.add('tenders-panel-open');
    return () => document.documentElement.classList.remove('tenders-panel-open');
  }, []);

  return (
    <div className="tenders-scrim" onClick={close} data-testid="tenders-scrim">
      <div
        ref={panel}
        className="tenders-panel"
        role="dialog"
        aria-modal="true"
        aria-label={posting?.title ?? 'פרטי הפרסום'}
        tabIndex={-1}
        // The scrim closes on click; the panel must not, or every click inside it dismisses.
        onClick={(event) => event.stopPropagation()}
      >
        <header className="tenders-panel__head">
          <button type="button" className="tenders-panel__close" onClick={close} aria-label="סגירה">
            ×
          </button>
          {/* Painted from the fetched posting only — the list row is not consulted, so a panel
              opened from a shared link and one opened from the list load identically. */}
          <h2 className="tenders-panel__title">{posting?.title ?? ' '}</h2>
        </header>

        <div className="tenders-panel__body">
          <OutcomeView outcome={outcome} onShowPlans={ctx.showPlans} onRetry={reload}>
            {posting === null ? <DetailSkeleton /> : <PostingBody posting={posting} />}
          </OutcomeView>
        </div>
      </div>
    </div>
  );
}
